<template>
  <div data-private class="ah">
    <div class="ah-card">
      <div class="ah-head">
        <div class="ah-pick">
          <label class="ah-pick-label" for="ah-report-a">Earlier report</label>
          <select id="ah-report-a" v-model="selectedReportA" class="ah-select" :disabled="loading || !sortedReports.length" @change="fetchComparison">
            <option v-for="report in sortedReports" :key="report.key" :value="report.key">
              {{ demoLabel(report.contract_id, 'contract') }} · {{ formatReportDate(report.report_dt) }} · {{ report.patient_count?.toLocaleString() }} patients
            </option>
          </select>
        </div>
        <span class="ah-arrow" aria-hidden="true">
          <svg viewBox="0 0 24 12" width="24" height="12"><path d="M0 6h21M16 1l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </span>
        <div class="ah-pick">
          <label class="ah-pick-label" for="ah-report-b">Later report</label>
          <select id="ah-report-b" v-model="selectedReportB" class="ah-select" :disabled="loading || !sortedReports.length" @change="fetchComparison">
            <option v-for="report in sortedReports" :key="report.key" :value="report.key">
              {{ demoLabel(report.contract_id, 'contract') }} · {{ formatReportDate(report.report_dt) }} · {{ report.patient_count?.toLocaleString() }} patients
            </option>
          </select>
        </div>
        <div class="ah-head-actions">
          <ExportMenu :disabled="!flows.length || loading" :exports="[{ type: 'excel', action: exportCSV, loading: exporting }]" />
        </div>
      </div>

      <LoadingIndicator v-if="loading" :message="sortedReports.length ? 'Comparing reports...' : 'Loading reports...'" />
      <p v-else-if="!sortedReports.length" class="ah-empty">No attribution reports for this entity yet.</p>
      <p v-else-if="!flows.length" class="ah-empty">Nothing to compare for these two reports. Pick different dates.</p>

      <template v-else>
        <div class="ah-figures">
          <div class="ah-figure">
            <div class="ah-figure-n">{{ reportAData.total_patients?.toLocaleString() || 0 }}</div>
            <div class="ah-figure-l">patients in {{ formatReportDate(reportAData.date) }}</div>
          </div>
          <div class="ah-figure ah-figure-net" :class="netChange >= 0 ? 'is-up' : 'is-down'">
            <div class="ah-figure-n">{{ netChange >= 0 ? '+' : '−' }}{{ Math.abs(netChange).toLocaleString() }}</div>
            <div class="ah-figure-l">{{ netChangePercent >= 0 ? '+' : '−' }}{{ Math.abs(netChangePercent) }}% net change</div>
          </div>
          <div class="ah-figure">
            <div class="ah-figure-n">{{ reportBData.total_patients?.toLocaleString() || 0 }}</div>
            <div class="ah-figure-l">patients in {{ formatReportDate(reportBData.date) }}</div>
          </div>
        </div>

        <div class="ah-ledger" role="img" :aria-label="ledgerSummary">
          <span v-for="seg in ledgerSegments" :key="seg.key" class="ah-ledger-seg" :style="{ width: seg.pct + '%', background: seg.color }" :title="`${seg.label}: ${seg.value.toLocaleString()}`"></span>
        </div>
        <div class="ah-ledger-key">
          <span v-for="seg in ledgerSegments" :key="seg.key" class="ah-ledger-k">
            <i :style="{ background: seg.color }"></i>{{ seg.label }} <b>{{ seg.value.toLocaleString() }}</b>
          </span>
        </div>
      </template>
    </div>

    <div v-if="sankeyData && !loading" class="ah-card">
      <h3 class="ah-title">Patient flow</h3>
      <div class="ah-sankey">
        <SankeyDiagram :chart-data="sankeyData" :config="sankeyConfig" />
      </div>
    </div>

    <div v-if="flows.length > 0 && !loading" class="ah-cols">
      <div class="ah-card ah-col">
        <div class="ah-col-head">
          <h3 class="ah-title">Lost</h3>
          <span class="ah-col-n is-down">{{ totalLost.toLocaleString() }}</span>
        </div>
        <p class="ah-col-sub">In the earlier report, not the later one</p>
        <button v-for="flow in lostFlows" :key="flow.target" type="button" class="ah-row" @click="viewPatients(flow)">
          <span class="ah-row-swatch" :style="{ background: flowColor(flow.target) }"></span>
          <span class="ah-row-text">
            <span class="ah-row-label">{{ getLossLabel(flow.target) }}</span>
            <span class="ah-row-desc">{{ getLossDescription(flow.target) }}</span>
          </span>
          <span class="ah-row-num">
            <b>{{ flow.value.toLocaleString() }}</b>
            <small>{{ ((flow.value / totalLost) * 100).toFixed(1) }}% of lost</small>
          </span>
          <span class="ah-row-go" aria-hidden="true">›</span>
        </button>
        <button v-if="internalShiftFlow" type="button" class="ah-row ah-row-aside" @click="viewPatients(internalShiftFlow)">
          <span class="ah-row-swatch" :style="{ background: flowColor('lost_internal_shift') }"></span>
          <span class="ah-row-text">
            <span class="ah-row-label">Moved within {{ scopeLabel }}</span>
            <span class="ah-row-desc">Attribution was lost, but claims show the patient now sees another provider in {{ scopeLabel }}. Shown apart from the losses above.</span>
          </span>
          <span class="ah-row-num"><b>{{ internalShiftFlow.value.toLocaleString() }}</b></span>
          <span class="ah-row-go" aria-hidden="true">›</span>
        </button>
      </div>

      <div class="ah-card ah-col">
        <div class="ah-col-head">
          <h3 class="ah-title">Kept and gained</h3>
          <span class="ah-col-n is-up">{{ ((keptFlow?.value || 0) + totalGained).toLocaleString() }}</span>
        </div>
        <p class="ah-col-sub">Everyone in the later report</p>
        <button v-if="keptFlow" type="button" class="ah-row" @click="viewPatients(keptFlow)">
          <span class="ah-row-swatch" :style="{ background: KEPT_COLOR }"></span>
          <span class="ah-row-text">
            <span class="ah-row-label">Kept</span>
            <span class="ah-row-desc">In both reports</span>
          </span>
          <span class="ah-row-num">
            <b>{{ keptFlow.value.toLocaleString() }}</b>
            <small>{{ ((keptFlow.value / reportAData.total_patients) * 100).toFixed(1) }}% retained</small>
          </span>
          <span class="ah-row-go" aria-hidden="true">›</span>
        </button>
        <button v-if="newFlow" type="button" class="ah-row" @click="viewPatients(newFlow)">
          <span class="ah-row-swatch" :style="{ background: flowColor('new') }"></span>
          <span class="ah-row-text">
            <span class="ah-row-label">New</span>
            <span class="ah-row-desc">First time attributed to this entity</span>
          </span>
          <span class="ah-row-num">
            <b>{{ newFlow.value.toLocaleString() }}</b>
            <small>{{ ((newFlow.value / reportBData.total_patients) * 100).toFixed(1) }}% of later panel</small>
          </span>
          <span class="ah-row-go" aria-hidden="true">›</span>
        </button>
        <button v-if="returningFlow" type="button" class="ah-row" @click="viewPatients(returningFlow)">
          <span class="ah-row-swatch" :style="{ background: flowColor('returning') }"></span>
          <span class="ah-row-text">
            <span class="ah-row-label">Returning</span>
            <span class="ah-row-desc">Previously attributed, left, now back</span>
          </span>
          <span class="ah-row-num">
            <b>{{ returningFlow.value.toLocaleString() }}</b>
            <small>{{ ((returningFlow.value / reportBData.total_patients) * 100).toFixed(1) }}% of later panel</small>
          </span>
          <span class="ah-row-go" aria-hidden="true">›</span>
        </button>
      </div>
    </div>

    <!-- Patient List Modal -->
    <div v-if="showPatientModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closePatientModal">
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" @click="closePatientModal"></div>

        <div class="relative bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:w-full sm:max-w-[90vw] text-left">
          <!-- Modal Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">{{ modalTitle }}</h3>
              <p class="text-sm text-gray-500 mt-1">{{ modalSubtitle }}</p>
            </div>
            <button class="text-gray-400 hover:text-gray-500" @click="closePatientModal">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Modal Content -->
          <div class="px-6 py-4 max-h-[60vh] overflow-y-auto">
            <LoadingIndicator v-if="loadingPatients" message="Loading patients..." />

            <table v-else-if="patientList.length > 0" class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DOB</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MBI</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facility</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PCP</th>
                  <th v-if="selectedCategory === 'lost_competition' || selectedCategory === 'lost_internal_shift' || selectedCategory === 'lost_still_active'" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Competing/New Provider</th>
                  <th v-if="selectedCategory?.startsWith('lost_')" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visits After</th>
                  <th v-if="selectedCategory?.startsWith('lost_')" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Claim</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="patient in patientList" :key="patient.bene_mbi_id" class="hover:bg-gray-50">
                  <td class="px-4 py-3 whitespace-nowrap">
                    <div class="sensitive text-sm font-medium text-gray-900" data-spoof-kind="person">
                      {{ patient.bene_last_name }}, {{ patient.bene_first_name }}
                    </div>
                    <div v-if="patient.bene_death_dt" class="text-xs text-rose-500">
                      Deceased <span class="sensitive" data-spoof-kind="date">{{ formatDate(patient.bene_death_dt) }}</span>
                    </div>
                  </td>
                  <td class="sensitive px-4 py-3 whitespace-nowrap text-sm text-gray-500" data-spoof-kind="dob">
                    {{ formatDate(patient.bene_dob) }}
                  </td>
                  <td class="sensitive px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-mono" data-spoof-kind="identifier">
                    {{ patient.bene_mbi_id }}
                  </td>
                  <td class="sensitive px-4 py-3 whitespace-nowrap text-sm text-gray-500" data-spoof-kind="facility">
                    {{ patient.facility_name || '-' }}
                  </td>
                  <td class="sensitive px-4 py-3 whitespace-nowrap text-sm text-gray-500" data-spoof-kind="provider">
                    {{ patient.pcp_name || '-' }}
                  </td>
                  <td v-if="selectedCategory === 'lost_competition' || selectedCategory === 'lost_internal_shift' || selectedCategory === 'lost_still_active'" class="px-4 py-3 whitespace-nowrap">
                    <div v-if="patient.competing_npi" class="text-sm">
                      <div :class="{ lost_still_active: 'font-medium text-violet-600 sensitive', lost_internal_shift: 'font-medium text-teal-600 sensitive' }[selectedCategory] || 'font-medium text-rose-600 sensitive'" data-spoof-kind="provider">{{ patient.competing_provider_name || patient.competing_npi }}</div>
                      <div class="text-xs text-gray-500">
                        <span v-if="patient.competing_provider_city"><span class="sensitive" data-spoof-kind="address">{{ patient.competing_provider_city }}, {{ patient.competing_provider_state }}</span> · </span> {{ patient.competing_npi_visits }} visit{{ patient.competing_npi_visits !== 1 ? 's' : '' }}
                      </div>
                    </div>
                    <span v-else class="text-gray-400 text-sm">-</span>
                  </td>
                  <td v-if="selectedCategory?.startsWith('lost_')" class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    <span v-if="patient.visits_after_loss > 0" class="text-amber-600">
                      {{ patient.visits_after_loss }} ({{ patient.visits_at_original_tin || 0 }} here)
                    </span>
                    <span v-else class="text-gray-400">0</span>
                  </td>
                  <td v-if="selectedCategory?.startsWith('lost_')" class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {{ patient.last_claim_date ? formatDate(patient.last_claim_date) : '-' }}
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-else class="text-center py-12 text-gray-500 text-sm">
              No patients found for this category
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div class="text-sm text-gray-500">
              Showing {{ patientList.length }} patients
              <span v-if="patientList.length >= 500" class="text-amber-600">(limited to 500)</span>
            </div>
            <button
              class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200"
              @click="closePatientModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import SankeyDiagram from '@/components/widgetcomponents/Viz_Elements/Sankey_Diagram.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { exportToExcel } from '@/utils/exportArraytoXlsx.js';
import { demoLabel } from '@/utils/demoSpoof';
import { formatCalendarDate } from '@/utils/common';

const FLOW_CATEGORIES = {
  retained: {
    title: 'Kept Patients',
  },
  new: {
    title: 'New Patients',
    color: '#3b82f6',       // blue-500
  },
  returning: {
    title: 'Returning Patients',
    color: '#06b6d4',       // cyan-500
  },
  lost_deceased: {
    title: 'Lost: Deceased',
    label: 'Deceased',
    description: 'Patient deceased',
    color: '#64748b',       // slate-500
  },
  lost_internal_shift: {
    title: 'Internal Provider Change',
    label: 'Internal Provider Change',
    description: 'Attribution was lost, but the patient now sees another provider in {scope}',
    color: '#0d9488',       // teal-600
  },
  lost_competition: {
    title: 'Lost: Competition',
    label: 'Competition',
    description: 'CMS or claims show the patient\'s care moved outside {scope}',
    color: '#f43f5e',       // rose-500
  },
  lost_disengaged: {
    title: 'Lost: Disengaged',
    label: 'Disengaged',
    description: 'Attribution was lost and there are no claims since the earlier report',
    color: '#fbbf24',       // amber-400
  },
  lost_still_active: {
    title: 'Lost: Still Receiving Care Here',
    label: 'Still Receiving Care Here',
    description: 'Attribution was lost, but claims show continued care within {scope}',
    color: '#8b5cf6',       // violet-500 - stands out as anomaly
  },
  lost_unknown: {
    title: 'Lost: Unknown Reason',
    label: 'Unknown',
    description: 'No claims data available to determine reason',
    color: '#9ca3af',       // gray-400
  },
};

const KEPT_COLOR = '#389A6C';

export default {
  name: 'Attribution_Health',
  components: {
    SankeyDiagram,
    ExportMenu,
    LoadingIndicator,
  },
  props: {
    entityId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      KEPT_COLOR,
      loading: false,
      availableReports: [],  // Full list of contract+report objects from API
      selectedReportA: null,  // Now stores "CONTRACT_ID|REPORT_DT" key
      selectedReportB: null,  // Now stores "CONTRACT_ID|REPORT_DT" key
      reportAData: null,
      reportBData: null,
      flows: [],
      // Patient modal state
      showPatientModal: false,
      loadingPatients: false,
      patientList: [],
      selectedCategory: null,
      selectedFlow: null,
      exporting: false
    };
  },
  computed: {
    ledgerSegments() {
      if (!this.flows.length || !this.reportAData) return [];
      const segs = [];
      if (this.keptFlow) segs.push({ key: 'kept', label: 'Kept', value: this.keptFlow.value, color: KEPT_COLOR });
      this.lostFlows.forEach((f) => segs.push({ key: f.target, label: this.getLossLabel(f.target), value: f.value, color: this.flowColor(f.target) }));
      if (this.newFlow) segs.push({ key: 'new', label: 'New', value: this.newFlow.value, color: this.flowColor('new') });
      if (this.returningFlow) segs.push({ key: 'returning', label: 'Returning', value: this.returningFlow.value, color: this.flowColor('returning') });
      const total = segs.reduce((n, s) => n + s.value, 0) || 1;
      return segs.filter((s) => s.value > 0).map((s) => ({ ...s, pct: (s.value / total) * 100 }));
    },
    ledgerSummary() {
      return this.ledgerSegments.map((s) => `${s.label} ${s.value.toLocaleString()}`).join(', ');
    },
    // All reports sorted by date descending, with a unique key
    sortedReports() {
      return this.availableReports
        .map(r => ({
          ...r,
          key: `${r.contract_id}|${r.report_dt}`
        }))
        .sort((a, b) => new Date(b.report_dt) - new Date(a.report_dt));
    },
    // Parse selected report A into contract_id and report_dt
    parsedReportA() {
      if (!this.selectedReportA) return null;
      const [contract_id, report_dt] = this.selectedReportA.split('|');
      return { contract_id, report_dt };
    },
    // Parse selected report B into contract_id and report_dt
    parsedReportB() {
      if (!this.selectedReportB) return null;
      const [contract_id, report_dt] = this.selectedReportB.split('|');
      return { contract_id, report_dt };
    },
    keptFlow() {
      return this.flows.find(f => f.category === 'retained');
    },
    newFlow() {
      return this.flows.find(f => f.category === 'new');
    },
    returningFlow() {
      return this.flows.find(f => f.category === 'returning');
    },
    lostFlows() {
      return this.flows.filter(f => f.category === 'lost' && f.target !== 'lost_internal_shift').sort((a, b) => b.value - a.value);
    },
    internalShiftFlow() {
      return this.flows.find(f => f.target === 'lost_internal_shift');
    },
    internalShiftLoss() {
      return this.internalShiftFlow ? this.internalShiftFlow.value : 0;
    },
    competitionLoss() {
      const flow = this.lostFlows.find(f => f.target === 'lost_competition');
      return flow ? flow.value : 0;
    },
    disengagedLoss() {
      const flow = this.lostFlows.find(f => f.target === 'lost_disengaged');
      return flow ? flow.value : 0;
    },
    stillActiveLoss() {
      const flow = this.lostFlows.find(f => f.target === 'lost_still_active');
      return flow ? flow.value : 0;
    },
    totalLost() {
      return this.lostFlows.reduce((sum, f) => sum + f.value, 0);
    },
    totalGained() {
      return (this.newFlow?.value || 0) + (this.returningFlow?.value || 0);
    },
    scopeLabel() {
      return this.entityId ? 'this practice' : 'your organization';
    },
    visitsHeader() {
      return this.entityId ? 'Visits at This Practice' : 'Visits in Your Organization';
    },
    modalTitle() {
      if (!this.selectedCategory) return '';
      return FLOW_CATEGORIES[this.selectedCategory]?.title || this.selectedCategory;
    },
    modalSubtitle() {
      if (!this.selectedFlow) return '';
      return `${this.selectedFlow.value?.toLocaleString()} patients`;
    },
    netChange() {
      if (!this.reportAData || !this.reportBData) return 0;
      return this.reportBData.total_patients - this.reportAData.total_patients;
    },
    netChangePercent() {
      if (!this.reportAData || this.reportAData.total_patients === 0) return 0;
      return ((this.netChange / this.reportAData.total_patients) * 100).toFixed(1);
    },
    sankeyData() {
      if (!this.flows.length || !this.reportAData || !this.reportBData) return null;

      const nodes = [];
      const links = [];
      const nodeMap = new Map();
      let nodeIndex = 0;

      // Helper to get or create node
      const getNodeIndex = (id, name, color) => {
        if (nodeMap.has(id)) {
          return nodeMap.get(id);
        }
        const index = nodeIndex++;
        nodeMap.set(id, index);
        nodes.push({ name, color });
        return index;
      };

      // Create nodes
      const reportAIndex = getNodeIndex('report_a', `${this.formatReportDate(this.reportAData.date)}\n${this.reportAData.total_patients.toLocaleString()} patients`, '#6366f1');
      const reportBIndex = getNodeIndex('report_b', `${this.formatReportDate(this.reportBData.date)}\n${this.reportBData.total_patients.toLocaleString()} patients`, '#6366f1');

      // Process flows and create links
      this.flows.forEach(flow => {
        if (flow.category === 'retained') {
          // Report A → Report B (kept)
          links.push({
            source: reportAIndex,
            target: reportBIndex,
            value: flow.value
          });
        } else if (flow.category === 'lost') {
          // Report A → Loss category
          const lossNodeIndex = getNodeIndex(flow.target, `${this.getLossLabel(flow.target)}\n${flow.value.toLocaleString()}`, FLOW_CATEGORIES[flow.target]?.color || '#9ca3af');
          links.push({
            source: reportAIndex,
            target: lossNodeIndex,
            value: flow.value
          });
        } else if (flow.category === 'new') {
          // New → Report B
          const newNodeIndex = getNodeIndex('new', `New\n${flow.value.toLocaleString()}`, FLOW_CATEGORIES.new.color);
          links.push({
            source: newNodeIndex,
            target: reportBIndex,
            value: flow.value
          });
        } else if (flow.category === 'returning') {
          // Returning → Report B
          const returningNodeIndex = getNodeIndex('returning', `Returning\n${flow.value.toLocaleString()}`, FLOW_CATEGORIES.returning.color);
          links.push({
            source: returningNodeIndex,
            target: reportBIndex,
            value: flow.value
          });
        }
      });

      return {
        nodes,
        links,
        stageLabels: [
          { label: 'Report A', position: 'left' },
          { label: 'Report B', position: 'right' }
        ]
      };
    },
    sankeyConfig() {
      return {
        margin: { top: 60, right: 200, bottom: 40, left: 200 },
        nodeWidth: 20,
        nodePadding: 30,
        iterations: 32,
        linkOpacity: 0.4,
        formatting: {
          percentage: false,
          sig_figs: 0
        }
      };
    }
  },
  mounted() {
    this.fetchComparison();
  },
  methods: {
    flowColor(target) {
      return FLOW_CATEGORIES[target]?.color || '#9ca3af';
    },
    demoLabel,
    async fetchComparison() {
      this.loading = true;
      try {
        const payload = {
          entity_id: this.entityId
        };

        // Add parsed report A params
        if (this.parsedReportA) {
          payload.contract_a_id = this.parsedReportA.contract_id;
          payload.report_a_date = this.parsedReportA.report_dt;
        }

        // Add parsed report B params
        if (this.parsedReportB) {
          payload.contract_b_id = this.parsedReportB.contract_id;
          payload.report_b_date = this.parsedReportB.report_dt;
        }

        const response = await this.$axios.post('/api/portfolio_management/compare-reports/', payload);

        this.reportAData = response.data.report_a;
        this.reportBData = response.data.report_b;
        this.flows = response.data.flows;

        // Set available reports if returned
        if (response.data.available_reports) {
          this.availableReports = response.data.available_reports;

          // Sync dropdown selections to match the reports the backend actually used
          if (response.data.report_a) {
            this.selectedReportA = `${response.data.report_a.contract_id}|${response.data.report_a.date}`;
          }
          if (response.data.report_b) {
            this.selectedReportB = `${response.data.report_b.contract_id}|${response.data.report_b.date}`;
          }
        }
      } catch (error) {
        console.error('Error fetching report comparison:', error);
      } finally {
        this.loading = false;
      }
    },

    formatReportDate(dateStr) {
      return this.formatDate(dateStr);
    },

    getLossLabel(target) {
      return FLOW_CATEGORIES[target]?.label || target;
    },

    getLossDescription(target) {
      return (FLOW_CATEGORIES[target]?.description || '').replace('{scope}', this.scopeLabel);
    },

    async viewPatients(flow) {
      // Determine category from flow
      let category;
      if (flow.category === 'lost') {
        category = flow.target;  // e.g., 'lost_competition'
      } else {
        category = flow.category;  // e.g., 'retained', 'new', 'returning'
      }

      this.selectedCategory = category;
      this.selectedFlow = flow;
      this.showPatientModal = true;
      this.loadingPatients = true;
      this.patientList = [];

      try {
        const payload = {
          entity_id: this.entityId,
          contract_a_id: this.parsedReportA?.contract_id,
          contract_b_id: this.parsedReportB?.contract_id,
          report_a_date: this.parsedReportA?.report_dt,
          report_b_date: this.parsedReportB?.report_dt,
          category: category,
          limit: 500
        };

        const response = await this.$axios.post('/api/portfolio_management/flow-patients/', payload);
        this.patientList = response.data;
      } catch (error) {
        console.error('Error fetching patients:', error);
        this.patientList = [];
      } finally {
        this.loadingPatients = false;
      }
    },

    closePatientModal() {
      this.showPatientModal = false;
      this.patientList = [];
      this.selectedCategory = null;
      this.selectedFlow = null;
    },

    formatDate(dateStr) {
      return formatCalendarDate(dateStr, 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    async exportCSV() {
      if (this.exporting || !this.flows.length) return;
      this.exporting = true;

      try {
        const response = await this.$axios.post('/api/portfolio_management/flow-patients/', {
          entity_id: this.entityId,
          contract_a_id: this.parsedReportA?.contract_id,
          contract_b_id: this.parsedReportB?.contract_id,
          report_a_date: this.parsedReportA?.report_dt,
          report_b_date: this.parsedReportB?.report_dt,
          category: 'all',
        });

        const rows = response.data.map(p => ({
          'Category': FLOW_CATEGORIES[p.flow_category]?.title || p.flow_category,
          'Last Name': p.bene_last_name || '',
          'First Name': p.bene_first_name || '',
          'DOB': p.bene_dob?.split('T')[0] || '',
          'Death Date': p.bene_death_dt?.split('T')[0] || '',
          'MBI': p.bene_mbi_id || '',
          'Facility': p.facility_name || '',
          'PCP': p.pcp_name || '',
          'Competing/New Provider': p.competing_provider_name || '',
          'Competing/New NPI': p.competing_npi || '',
          'Visits After Loss': p.visits_after_loss ?? '',
          [this.visitsHeader]: p.visits_at_original_tin ?? '',
          'Last Claim Date': p.last_claim_date?.split('T')[0] || '',
          'Assignment Type': p.origin || '',
        }));

        const dateA = this.parsedReportA?.report_dt?.split(' ')[0];
        const dateB = this.parsedReportB?.report_dt?.split(' ')[0];
        const fileName = `attribution_health_${dateA}_${dateB}.xlsx`;
        await exportToExcel(rows, fileName, {
          sheetName: 'Attribution Health',
          columnWidths: {
            'Category': 140,
            'Last Name': 140,
            'First Name': 120,
            'DOB': 100,
            'Death Date': 100,
            'MBI': 120,
            'Facility': 175,
            'PCP': 175,
            'Competing/New Provider': 175,
            'Competing/New NPI': 105,
            'Visits After Loss': 105,
            [this.visitsHeader]: 105,
            'Last Claim Date': 100,
            'Assignment Type': 175,
          },
        });
      } catch (error) {
        console.error('Error exporting:', error);
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>

<style scoped>
.ah { display: flex; flex-direction: column; gap: 16px; }
.ah-card { background: #fff; border: 1px solid var(--color-gray-200); border-radius: 10px; padding: 20px 22px; }
.ah-title { margin: 0; font-size: 14px; font-weight: 600; color: var(--color-gray-900); letter-spacing: -0.01em; }
.ah-empty { margin: 8px 0 0; font-size: 13px; color: var(--color-gray-500); }

.ah-head { display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto; align-items: end; gap: 14px; }
.ah-pick { min-width: 0; }
.ah-pick-label { display: block; font-size: 12px; color: var(--color-gray-500); margin-bottom: 6px; }
.ah-select { width: 100%; height: 36px; padding: 0 32px 0 12px; font: inherit; font-size: 13px; color: var(--color-gray-900); background: #fff; border: 1px solid var(--color-gray-300); border-radius: 8px; appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' fill='none' stroke='%236b7280' stroke-width='1.6' stroke-linecap='round'/></svg>"); background-repeat: no-repeat; background-position: right 12px center; }
.ah-select:focus-visible { outline: 2px solid var(--color-accent); outline-offset: 1px; }
.ah-select:disabled { color: var(--color-gray-400); }
.ah-arrow { color: var(--color-gray-400); padding-bottom: 10px; }
.ah-head-actions { padding-bottom: 2px; }

.ah-figures { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 24px; margin-top: 24px; }
.ah-figure-n { font-size: 30px; font-weight: 600; line-height: 1; letter-spacing: -0.02em; color: var(--color-gray-900); font-variant-numeric: tabular-nums; }
.ah-figure-l { margin-top: 6px; font-size: 12px; color: var(--color-gray-500); }
.ah-figure:last-child { text-align: right; }
.ah-figure-net { text-align: center; padding: 10px 22px; border-radius: 999px; background: var(--color-gray-50); }
.ah-figure-net.is-down .ah-figure-n { color: var(--color-red-dark); }
.ah-figure-net.is-up .ah-figure-n { color: var(--color-green-dark); }

.ah-ledger { display: flex; height: 14px; margin-top: 22px; border-radius: 7px; overflow: hidden; background: var(--color-gray-100); }
.ah-ledger-seg { display: block; height: 100%; min-width: 2px; }
.ah-ledger-seg + .ah-ledger-seg { box-shadow: -1px 0 0 #fff; }
.ah-ledger-key { display: flex; flex-wrap: wrap; gap: 6px 18px; margin-top: 10px; font-size: 12px; color: var(--color-gray-600); }
.ah-ledger-k i { display: inline-block; width: 8px; height: 8px; border-radius: 2px; margin-right: 6px; vertical-align: 0; }
.ah-ledger-k b { font-weight: 600; color: var(--color-gray-900); font-variant-numeric: tabular-nums; }

.ah-sankey { height: 500px; position: relative; margin-top: 8px; }

.ah-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.ah-col { padding: 18px 20px 8px; }
.ah-col-head { display: flex; align-items: baseline; justify-content: space-between; }
.ah-col-n { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; }
.ah-col-n.is-down { color: var(--color-red-dark); }
.ah-col-n.is-up { color: var(--color-green-dark); }
.ah-col-sub { margin: 2px 0 10px; font-size: 12px; color: var(--color-gray-500); }

.ah-row { display: grid; grid-template-columns: 10px minmax(0, 1fr) auto 12px; align-items: center; gap: 12px; width: 100%; padding: 12px 0; text-align: left; background: none; border: 0; border-top: 1px solid var(--color-gray-100); font: inherit; color: inherit; cursor: pointer; }
.ah-row:hover .ah-row-label, .ah-row:focus-visible .ah-row-label { color: var(--color-accent); }
.ah-row:focus-visible { outline: 2px solid var(--color-accent); outline-offset: -2px; border-radius: 6px; }
.ah-row-aside { background: var(--color-gray-50); margin: 6px -20px -8px; padding: 12px 20px; border-top: 0; border-radius: 0 0 10px 10px; }
.ah-row-swatch { width: 10px; height: 10px; border-radius: 3px; }
.ah-row-text { min-width: 0; }
.ah-row-label { display: block; font-size: 13px; font-weight: 600; color: var(--color-gray-900); }
.ah-row-desc { display: block; font-size: 12px; color: var(--color-gray-500); line-height: 1.4; }
.ah-row-num { text-align: right; white-space: nowrap; }
.ah-row-num b { display: block; font-size: 16px; font-weight: 600; color: var(--color-gray-900); font-variant-numeric: tabular-nums; }
.ah-row-num small { display: block; font-size: 11px; color: var(--color-gray-500); }
.ah-row-go { color: var(--color-gray-400); font-size: 16px; line-height: 1; }

@media (max-width: 900px) {
  .ah-head { grid-template-columns: 1fr; }
  .ah-arrow { display: none; }
  .ah-figures { grid-template-columns: 1fr; text-align: left; }
  .ah-figure:last-child { text-align: left; }
  .ah-cols { grid-template-columns: 1fr; }
}
</style>
